import { useState } from 'react';
import { BusinessCardCustomization, BusinessCardLocationData } from '../types/cart';
import { BusinessCardField } from '../types';
import { CustomizeItemForm } from './customize-item/CustomizeItemForm';
import { CustomizeItemPreview } from './customize-item/CustomizeItemPreview';

interface CustomizeItemCustomizerProps {
  onCustomizationChange: (customization: BusinessCardCustomization) => void;
  initialCustomization?: BusinessCardCustomization;
  productImages?: string[];
  previewImage?: string | null;
  withBorder?: boolean;
  fields?: BusinessCardField[];
  options?: { color?: string; text_message?: string };
}

const PHONE_FIELDS = ['phone', 'cell_phone', 'fax', 'customer_service_phone', 'toll_free'];

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function CustomizeItemCustomizer({
  onCustomizationChange,
  initialCustomization,
  productImages,
  previewImage,
  withBorder = true,
  fields = [],
  options,
}: CustomizeItemCustomizerProps) {
  const cardClass = withBorder ? 'bg-white rounded-xl shadow-lg border border-gray-200 p-6' : '';
  const [data, setData] = useState<BusinessCardLocationData>(initialCustomization || {});

  const handleFieldChange = (field: string, value: string) => {
    const processed = PHONE_FIELDS.includes(field) ? formatPhoneNumber(value) : value;
    const updated = { ...data, [field]: processed };
    setData(updated);
    onCustomizationChange(updated as BusinessCardCustomization);
  };

  return (
    <div className="space-y-8">
      {options?.text_message && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          {options.text_message}
        </div>
      )}

      <div className={cardClass}>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Customize Your Item</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CustomizeItemForm data={data} fields={fields} onFieldChange={handleFieldChange} />
          <CustomizeItemPreview
            productImages={productImages}
            previewImage={previewImage}
            data={data}
            fields={fields}
            fontColor={options?.color}
          />
        </div>
      </div>
    </div>
  );
}
